import type { MultiKey } from '../core/MultiKey';
import type { ScenarioLocation } from '../../lib/planningData';
import type { Distances, RouteData, RouteParams } from './context';
import type { JSONSpecialObject } from '../core/ExtendedJSON';
import { zeroDistance } from './constant';
import {
  parseLocationCoordinates,
  resolveEffectiveLocation,
} from './distance-utils';

export class DistanceMap implements Distances {
  map: MultiKey<RouteData, RouteParams>;
  locationsById: Map<number, ScenarioLocation>;
  draft: number | null;
  noGoZones: string;

  constructor(
    map: MultiKey<RouteData, RouteParams>,
    locationsById: Map<number, ScenarioLocation>,
    draft: number | null | undefined,
    noGoZones: string,
  ) {
    this.map = map;
    this.locationsById = locationsById;
    this.draft = draft ?? null;
    this.noGoZones = noGoZones ?? null;
  }

  toJS(): JSONSpecialObject {
    return {
      __type: 'DistanceMap',
      value: {
        map: this.map,
        locationsById: this.locationsById,
        draft: this.draft,
        noGoZones: this.noGoZones,
      },
    };
  }

  static fromJS(
    value: (JSONSpecialObject & { __type: 'DistanceMap' })['value'],
  ): DistanceMap {
    return new DistanceMap(
      value.map,
      value.locationsById,
      value.draft,
      value.noGoZones,
    );
  }

  /**
   * Finds route data between two locations in both normal and reverse directions.
   * Returns:
   * - NORMAL_ROUTE if it has an override
   * - REVERSE_ROUTE if it has an override and NORMAL_ROUTE does not
   * - Otherwise returns NORMAL_ROUTE
   *
   * Priority order:
   * 1. NORMAL_ROUTE with override
   * 2. REVERSE_ROUTE with override
   * 3. Default NORMAL_ROUTE
   */
  private findRouteData(
    fromLocation: ScenarioLocation,
    toLocation: ScenarioLocation,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): RouteData | undefined {
    const fromCoordinates = parseLocationCoordinates(fromLocation);
    const toCoordinates = parseLocationCoordinates(toLocation);
    if (!fromCoordinates || !toCoordinates) {
      return zeroDistance;
    }

    const { longitude: fLong, latitude: fLat } = fromCoordinates;
    const { longitude: tLong, latitude: tLat } = toCoordinates;

    const normalRoute = this.map.get(
      fLong,
      fLat,
      tLong,
      tLat,
      avoidsChannels,
      draft ?? this.draft,
      noGoZones ?? this.noGoZones,
    );

    if (normalRoute?.is_override) {
      return normalRoute;
    }

    const reverseRoute = this.map.get(
      tLong,
      tLat,
      fLong,
      fLat,
      avoidsChannels,
      draft ?? this.draft,
      noGoZones ?? this.noGoZones,
    );

    return reverseRoute?.is_override ? reverseRoute : normalRoute;
  }

  get(
    fromLocationId: number,
    toLocationId: number,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): RouteData | undefined {
    const fromLocation = resolveEffectiveLocation(
      this.locationsById,
      fromLocationId,
    );
    const toLocation = resolveEffectiveLocation(
      this.locationsById,
      toLocationId,
    );

    if (!fromLocation || !toLocation) {
      return undefined;
    }

    if (fromLocation === toLocation) {
      return zeroDistance;
    }

    return this.findRouteData(
      fromLocation,
      toLocation,
      avoidsChannels,
      draft,
      noGoZones,
    );
  }

  set(
    data: RouteData,
    fromLocationId: number,
    toLocationId: number,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): void {
    const from = this.locationsById.get(fromLocationId);
    const to = this.locationsById.get(toLocationId);

    if (!from || !to || from === to) {
      return;
    }

    const fromCoords = parseLocationCoordinates(from);
    const toCoords = parseLocationCoordinates(to);

    if (!fromCoords || !toCoords) {
      return;
    }

    const { longitude: fLong, latitude: fLat } = fromCoords;
    const { longitude: tLong, latitude: tLat } = toCoords;

    const isOverride = data.is_override;
    const existingData = this.map.get(
      fLong,
      fLat,
      tLong,
      tLat,
      avoidsChannels,
      draft ?? this.draft,
      noGoZones ?? this.noGoZones,
    );

    // Only set the data if:
    // 1. There is no existing data, or
    // 2. The new data is an override, or
    // 3. The existing data is not an override
    if (!existingData || isOverride || !existingData.is_override) {
      this.map.set(
        data,
        fLong,
        fLat,
        tLong,
        tLat,
        avoidsChannels,
        draft ?? this.draft,
        noGoZones ?? this.noGoZones,
      );
    }
  }
}
